export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: "same-origin",
    headers: body !== undefined ? { "Content-Type": "application/json" } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const data: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const message =
      data && typeof data === "object" && "error" in data && typeof (data as { error: unknown }).error === "string"
        ? (data as { error: string }).error
        : "Terjadi kesalahan";
    throw new ApiError(res.status, message);
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body),
  del: <T>(path: string) => request<T>("DELETE", path),
};

export function qs(params: Record<string, string | number | undefined | null>): string {
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") sp.set(key, String(value));
  }
  const str = sp.toString();
  return str ? `?${str}` : "";
}

export interface AuthUser {
  id: string;
  username: string | null;
  email: string;
}

export interface UserAccount {
  id: string;
  username: string | null;
  email: string;
  created_at: number;
}

export interface ExcludedIp {
  ip: string;
  last_seen: number;
}

export interface Site {
  id: string;
  name: string;
  domain: string | null;
  tz: string;
  tz_offset: number;
  share_token: string | null;
  created_at: number;
}

export interface Totals {
  pageviews: number;
  visitors: number;
  visits: number;
  bounces: number;
  total_time: number;
}

export interface SeriesPoint extends Totals {
  ts: number;
}

export interface StatsResponse {
  totals: Totals;
  series: SeriesPoint[];
}

export interface DimRow {
  val: string;
  pageviews: number;
  visitors: number;
  visits: number;
  bounces: number;
  total_time: number;
}

export interface MetricsResponse {
  tables: Record<string, DimRow[]>;
  range: { from: number; to: number; unit: "hour" | "day" };
}

export interface RealtimeRow {
  val: string;
  pageviews: number;
  visitors: number;
}

export interface RealtimeResponse {
  visitors: number;
  pageviews: number;
  series: Array<{ t: number; pageviews: number; visitors: number }>;
  pages: RealtimeRow[];
  referrers: RealtimeRow[];
  countries: RealtimeRow[];
  events: RealtimeRow[];
}

export interface EventsResponse {
  events: Array<{ name: string; count: number; visitors: number; visits: number }>;
  range: { from: number; to: number; unit: "hour" | "day" };
}

export interface EventPropsResponse {
  name: string;
  props: Array<{ data: string | null; count: number; visitors: number }>;
}

export interface RangeParams {
  from: number;
  to: number;
  unit: "hour" | "day";
}

export function rangeQuery(range: RangeParams, limit?: number): string {
  return qs({ from: range.from, to: range.to, unit: range.unit, limit });
}
